<script setup lang="ts">
import Illustration from '@/modules/Workspaces/KnowledgeHub/Files/FileTableEmptyStateIllustration.svg'
import { invariant } from '@/shared/utils/typeAssertions'
import { useForm } from '@/sharedComposables/useForm'
import BrandedDialog from '@/uiKit/BrandedDialog'
import DarwinButton from '@/uiKit/DarwinButton.vue'
import TextArea from '@/uiKit/TextArea.vue'
import TextField from '@/uiKit/TextField.vue'
import { whenever } from '@vueuse/core'
import { computed, useId } from 'vue'

export type HubFormValues = {
  name: string
  description: string | null
}

const props = defineProps<{
  title: string
  description: string
  submitLabel: string
  isPending: boolean
  error?: Error | string
  initialValues?: HubFormValues
}>()

const emit = defineEmits<{
  submit: [values: HubFormValues]
}>()

const formId = useId()
const open = defineModel<boolean>({ required: true })

const errorMessage = computed(() => {
  if (!props.error) return null

  if (typeof props.error === 'string') return props.error
  if (props.error instanceof Error) return props.error.message

  return 'Unknown error occurred'
})

const requiredValidator = (value: string | boolean) => {
  return String(value).trim().length === 0 ? 'This field is required' : undefined
}

const {
  hasErrors,
  getFieldProps,
  values,
  errors,
  reset: clearForm,
  validate,
} = useForm({
  name: {
    value: props.initialValues?.name || '',
    validate: requiredValidator,
  },
  description: {
    value: props.initialValues?.description || '',
    validate: requiredValidator,
  },
})

whenever(
  () => open.value,
  () => {
    clearForm()
    values.name = props.initialValues?.name || ''
    values.description = props.initialValues?.description || ''
  },
)

function onSubmit(e: Event) {
  invariant(e.target instanceof HTMLFormElement)
  validate()
  if (hasErrors.value) return

  emit('submit', {
    name: values.name,
    description: values.description,
  })
}
</script>

<template>
  <BrandedDialog.Root v-model="open">
    <template #header>
      <BrandedDialog.Header
        :title="title"
        :description="description"
      />
    </template>

    <template #form>
      <form
        :id="formId"
        class="flex flex-col gap-2"
        autocomplete="off"
        @submit.prevent="onSubmit"
      >
        <TextField
          autofocus
          placeholder="Hub name"
          size="lg"
          name="name"
          :disabled="isPending"
          :error="errors.name"
          :value="values.name"
          v-bind="getFieldProps('name')"
        />
        <label class="flex flex-col gap-1.5">
          <TextArea
            placeholder="Set a description…"
            size="lg"
            disable-resize
            rows="10"
            name="description"
            :error="errors.description"
            v-bind="getFieldProps('description')"
            :disabled="isPending"
            :value="values.description"
          />
          <span class="text-sm-12px-light text-text-subtlest">
            Describe what this hub should be used for.
          </span>
        </label>
      </form>
      <p
        v-if="errorMessage"
        class="text-sm-12px-light text-text-critical"
      >
        {{ errorMessage }}
      </p>
    </template>

    <template #actions>
      <slot name="actions">
        <DarwinButton
          size="md"
          rounded
          :form="formId"
          type="submit"
          :disabled="isPending"
          variant="black"
        >
          {{ submitLabel }}
        </DarwinButton>
      </slot>
    </template>

    <template #aside>
      <div class="flex h-full flex-col items-center justify-center gap-3">
        <Illustration />
        <p class="text-center text-xl-18px-bold">From messy data to reusable knowledge.</p>
        <p class="text-center text-sm-12px-light text-text-subtle">
          Hubs index your data based on the provided description.<br />
          This allows you to search and interrogate your hubs within cases.
        </p>
      </div>
    </template>
  </BrandedDialog.Root>
</template>
